import {
  AnchorHTMLAttributes,
  HTMLAttributes,
  ImgHTMLAttributes,
  ReactElement,
  TableHTMLAttributes,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from 'react';
import remarkGfm from 'remark-gfm';
import Image from 'next/image';
import Link from 'next/link';
import { logging } from '@/lib/logging/LogManager';
import { extractPath } from '@/lib/logging/Logger';
import rehypeRaw from 'rehype-raw';

const logger = logging.getLogger('strapi', extractPath(__filename));

// Parses the JSON returned by a network request
export const parseJSON = (resp: { status?: number; json?: any }) =>
  resp.json ? resp.json() : resp;

// Checks if a network request came back fine, and throws an error if not
export const checkStatus = async (resp: Response, _name: string) => {
  if (resp.status >= 200 && resp.status < 300) {
    return resp;
  }
  throw resp;
};

type StrapiFields = Record<string, any>;
export type StrapiEntity<T extends StrapiFields = StrapiFields> = {
  id?: string | number;
  documentId?: string;
} & Partial<T>;

export const getStrapiHeaders = () => {
  const token = process.env.STRAPI_RO_TOKEN || process.env.NEXT_PUBLIC_STRAPI_RO_TOKEN;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export const headers = getStrapiHeaders();

const isNotFoundResponse = (err: unknown) =>
  Boolean(err && typeof err === 'object' && (err as Response).status === 404);

const toErrorPayload = (err: unknown) => {
  if (err instanceof Error) {
    return { name: err.name, message: err.message, stack: err.stack };
  }

  if (err && typeof err === 'object') {
    const maybeResp = err as Response;
    if (typeof maybeResp.status === 'number') {
      return { status: maybeResp.status, statusText: maybeResp.statusText, type: 'Response' };
    }
  }

  return { value: err };
};

type CustomParagraphProps = HTMLAttributes<HTMLParagraphElement>;

export const CustomParagraph = (props: CustomParagraphProps) => (
  <p {...props} className={`not-prose mb-4 text-md text-foreground ${props.className || ''}`}>
    {props.children}
  </p>
);

const CustomH1 = (props: HTMLAttributes<HTMLHeadingElement>) => (
  <h1 className="not-prose text-3xl text-foreground font-bold mb-4">{props.children}</h1>
);

const CustomH2 = (props: HTMLAttributes<HTMLHeadingElement>) => (
  <h2 className="not-prose text-2xl text-foreground font-bold mb-4">{props.children}</h2>
);

const CustomH3 = (props: HTMLAttributes<HTMLHeadingElement>) => (
  <h3 className="not-prose text-xl text-foreground font-bold mb-4">{props.children}</h3>
);

export const CustomH4 = (props: HTMLAttributes<HTMLHeadingElement>) => (
  <h4 className={`not-prose text-lg text-foreground font-bold mb-4 ${props.className || ''}`}>
    {props.children}
  </h4>
);

export const CustomH5 = (props: HTMLAttributes<HTMLHeadingElement>) => (
  <h5 className={`not-prose text-base text-foreground font-bold mb-4 ${props.className || ''}`}>
    {props.children}
  </h5>
);

const CustomH6 = (props: HTMLAttributes<HTMLHeadingElement>) => (
  <h6 className="not-prose text-sm text-foreground font-bold mb-4">{props.children}</h6>
);

const CustomEmphasis = (props: HTMLAttributes<HTMLElement>) => (
  <em className="not-prose text-foreground italic">{props.children}</em>
);

const CustomStrong = (props: HTMLAttributes<HTMLElement>) => (
  <strong className={`not-prose text-foreground font-bold ${props.className || ''}`}>
    {props.children}
  </strong>
);

const isInternalHref = (href: string) => href.startsWith('/') || href.startsWith('#');

const CustomLink = (props: AnchorHTMLAttributes<HTMLAnchorElement>) => {
  const href = props.href ?? '';
  const className = `not-prose text-foreground underline ${props.className || ''}`.trim();

  if (!href) {
    return <span className={className}>{props.children}</span>;
  }

  if (isInternalHref(href) && props.target !== '_blank') {
    return (
      <Link href={href} className={className}>
        {props.children}
      </Link>
    );
  }

  return (
    <a
      href={href}
      className={className}
      target={props.target || '_blank'}
      rel={props.rel || 'noopener noreferrer'}
    >
      {props.children}
    </a>
  );
};

const CustomTable = (props: TableHTMLAttributes<HTMLTableElement>) => (
  // <div className="overflow-x-auto">
  <table className="not-prose table-auto mb-4 text-foreground w-full text-xs sm:text-sm">
    {props.children}
  </table>
  // </div>
);

const CustomTableRow = (props: HTMLAttributes<HTMLTableRowElement>) => (
  <tr className="not-prose bg-gray-100 border-b border-gray-300 text-foreground">
    {props.children}
  </tr>
);

const CustomTableCell = (props: TdHTMLAttributes<HTMLTableCellElement>) => (
  <td className="not-prose px-2 sm:px-4 py-2 border-y border-gray-300 text-foreground">
    {props.children}
  </td>
);

const CustomTableHeader = (props: ThHTMLAttributes<HTMLTableCaptionElement>) => (
  <th className="not-prose pl-2 sm:pl-4 pr-4 pt-3 pb-3 border-l border-dashed text-foreground">
    {props.children}
  </th>
);

const CustomTableHead = (props: ThHTMLAttributes<HTMLTableCaptionElement>) => (
  <thead className="not-prose border rounded-lg">{props.children}</thead>
);

const CustomImage = (props: ImgHTMLAttributes<HTMLImageElement>) => {
  if (typeof props.src !== 'string') return null;

  return (
    <span className="my-4 relative">
      <Image
        className="not-prose w-full max-w-3xl p-1 h-auto mx-auto rounded-lg shadow-md border-2 border-gray-300 mb-4"
        src={props.src}
        alt={props.alt ?? ''}
        title={props.title}
        width="0"
        height="0"
        sizes="100vw"
      />
      {props.title && <p className="text-center text-sm mt-2 text-foreground">{props.title}</p>}
    </span>
  );
};

export const components = {
  p: CustomParagraph,
  em: CustomEmphasis,
  strong: CustomStrong,
  a: CustomLink,
  table: CustomTable,
  //tr: CustomTableRow,
  thead: CustomTableHead,
  td: CustomTableCell,
  th: CustomTableHeader,
  img: CustomImage,
  h1: CustomH1,
  h2: CustomH2,
  h3: CustomH3,
  h4: CustomH4,
  h5: CustomH5,
  h6: CustomH6,
  ul: ({ ...props }) => (
    <ul
      className="not-prose ps-6 mb-6 text-foreground"
      style={{
        display: 'block',
        listStyleType: 'disc',
        // paddingInlineStart: '2rem',
      }}
      {...props}
    />
  ),
  ol: ({ ...props }) => (
    <ol
      style={{
        display: 'block',
        listStyleType: 'decimal',
        // paddingInlineStart: '2rem',
      }}
      className="not-prose text-md ps-6 mb-6 text-foreground"
      {...props}
    />
  ),
};

export const reactMarkdownConfig = {
  components,
  remarkPlugins: [remarkGfm],
  rehypePlugins: [rehypeRaw],
};

export async function getFromCMS<T extends StrapiFields = StrapiFields>(
  name: string,
  locale?: string
): Promise<StrapiEntity<T>> {
  let queryParams = '';
  if (locale) {
    queryParams = `?locale=${locale}`;
  }
  const requestUrl = `${process.env.NEXT_PUBLIC_STRAPI_SERVER}/api/${name}${queryParams}`;
  try {
    const response = await fetch(requestUrl, {
      headers: getStrapiHeaders(),
      method: 'GET',
      next: { revalidate: 360 },
    });
    const statusResponse = await checkStatus(response, name);
    const json = await parseJSON(statusResponse);
    return json?.data ?? {};
  } catch (err) {
    if (!isNotFoundResponse(err)) {
      logger.error('getFromCMS failed', {
        name,
        locale,
        queryParams,
        requestUrl,
        error: toErrorPayload(err),
      });
    }
    return {};
  }
}

export async function getFromCMSCollection<T extends StrapiFields = StrapiFields>(
  name: string,
  locale?: string,
  extraQueryParams?: string[]
): Promise<StrapiEntity<T>[]> {
  let queryParams = '?sort[0]=publishedAt:desc';
  if (extraQueryParams) {
    queryParams = queryParams.concat('&', extraQueryParams.join('&'));
  }
  if (locale) {
    queryParams = `${queryParams}&locale=${locale}`;
  }
  const requestUrl = `${process.env.NEXT_PUBLIC_STRAPI_SERVER}/api/${name}${queryParams}`;

  try {
    const response = await fetch(requestUrl, {
      headers: getStrapiHeaders(),
      method: 'GET',
      next: { revalidate: 360 },
    });
    const statusResponse = await checkStatus(response, name);
    const json = await parseJSON(statusResponse);
    return Array.isArray(json?.data) ? json.data : [];
  } catch (err) {
    logger.error('getFromCMSCollection failed', {
      name,
      locale,
      queryParams,
      requestUrl,
      extraQueryParams,
      error: toErrorPayload(err),
    });
    return [];
  }
}

// This function takes a component (Comp) and returns a new component with additional className(s)
export const withCustomTextColor = (Comp: React.ElementType, additionalClassName: string) => {
  // Return a new functional component
  // eslint-disable-next-line react/display-name
  return (props: any): ReactElement => {
    // Merge the original className with the new one
    const className = `${props.className || ''} ${additionalClassName}`.trim();
    // Clone the component with the new className and any additional props

    return <Comp {...props} className={className} />;
  };
};
